<template>
  <qtm-dialog-card v-model="open" :title="`${modelValue ? 'Change' : 'Select'} Supplier Contact`" width="474">
    <template v-slot:activator="{ props: activator }">
      <slot name="activator" :props="activator">
        <qtm-icon-btn v-bind="activator" :class="activatorClass" icon="mdi-pencil" size="small" />
      </slot>
    </template>
    <qtm-input-label class="my-6" label="Supplier Contact">
      <qtm-autocomplete
        v-model="contact"
        hide-details
        :item-title="contactText"
        :items="contacts"
        :loading="loading"
        return-object
      >
        <template v-slot:item="{ item, props: itemProps}">
          <v-list-item v-bind="itemProps">
            <template v-slot:title>
              {{ contactText(item.raw) }}
              <out-of-office-status v-if="item.raw?.ooo_date_started" class="ml-2" />
            </template>
          </v-list-item>
        </template>
        <template v-slot:append-item>
          <v-list-item class="qtm-label text-interactive" @click="createContact = true">
            <v-icon class="mt-n1 mr-2">
              mdi-plus
            </v-icon>
            Add New
          </v-list-item>
        </template>
      </qtm-autocomplete>
      <create-contact-dialog
        v-model="createContact"
        email-required
        :vendor="vendor"
        @new-contact="addContact"
      />
    </qtm-input-label>

    <template v-slot:actions>
      <qtm-btn :loading="saving" size="x-large" tertiary @click="open = false">
        Cancel
      </qtm-btn>
      <qtm-btn :disabled="!contact || contact.id === modelValue?.id" :loading="saving" size="x-large" @click="onSave">
        {{ saveLabel }}
      </qtm-btn>
    </template>
  </qtm-dialog-card>
</template>

<script setup lang="ts">
import cloneDeep from 'lodash/cloneDeep'
import type { Contact, Vendor } from '@quotetome/materials-api'
import CreateContactDialog from '@/components/vendors/create-contact-dialog.vue'
import OutOfOfficeStatus from '@/components/vendors/out-of-office-status.vue'

export interface Props {
  activatorClass?: string
  modelValue?: Contact | null
  preferred?: boolean
  save?: (c: Contact) => Promise<void>
  saveLabel?: string
  vendor: Vendor
}

const props = withDefaults(defineProps<Props>(), {
  activatorClass: undefined,
  modelValue: undefined,
  save: undefined,
  saveLabel: 'Save',
})
const emit = defineEmits(['create-contact', 'update:model-value'])

const contact = ref()
const contacts = ref<Contact[]>([])
const createContact = ref(false)
const loading = ref(false)
const open = ref(false)
const saving = ref(false)

const contactText = (c?: Contact | null) => {
  if (!c) {
    return ''
  }

  return `${c.first_name} ${c.last_name} (${c.email})`
}

const authStore = useAuthStore()
const user = authStore.user!
const { $api, $error } = useNuxtApp()

const fetchContacts = async () => {
  if (!props.vendor) {
    contacts.value = []
    return
  }

  loading.value = true
  try {
    if (user.is_staff) {
      contacts.value = await $api.v1.vendors.contacts(props.vendor.id)
    }
    else {
      contacts.value = await $api.v1.vendors.availableContacts(
        props.vendor.id,
        { preferred: props.preferred || undefined}
      )
    }
  }
  catch (error) {
    $error.report(error)
  }
  loading.value = false
}

watch(open, () => {
  if (open.value) {
    contact.value = cloneDeep(props.modelValue)

    if (!contacts.value.length) {
      fetchContacts()
    }
  }
}, { immediate: true })

const addContact = (c: Contact) => {
  contact.value = c
  contacts.value.push(c)
  createContact.value = false
  onSave()
}

const onSave = async () => {
  saving.value = true
  try {
    if (props.save) {
      await props.save(contact.value!)
    }
    emit('update:model-value', contact.value)
    open.value = false
  }
  catch (error) {
    $error.report(error)
  }
  saving.value = false
}
</script>
